import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Target, Navigation, Users, Plus, X, Server, RefreshCw, Globe, Zap, Cpu, Activity, Mail, Trash2 } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';

interface ReconClient {
  id: string;
  name: string;
  status: string;
  value: string;
  location: string;
  sector: string;
  lastContact: string;
}

export function ReconnaissanceNode() {
  const [clients, setClients] = useState<ReconClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [sortByValueDesc, setSortByValueDesc] = useState(false);
  
  // Form state
  const [newName, setNewName] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newSector, setNewSector] = useState('');

  // Project Recon state
  const [reconInput, setReconInput] = useState('');
  const [isReconActive, setIsReconActive] = useState(false);
  const [reconLogs, setReconLogs] = useState<string[]>([]);
  const [reconReport, setReconReport] = useState<any | null>(null);
  const [reconSaved, setReconSaved] = useState(false);

  // Project Overlord state
  const [overlordInput, setOverlordInput] = useState('');
  const [isOverlordActive, setIsOverlordActive] = useState(false);
  const [overlordLogs, setOverlordLogs] = useState<string[]>([]);
  const [overlordReport, setOverlordReport] = useState<any | null>(null);
  const [overlordSaved, setOverlordSaved] = useState(false);
  const [overlordTab, setOverlordTab] = useState<'competitor' | 'workforce'>('competitor');

  // 3D Battlefield & Conflict Vector Strike Node State
  const [battlefieldTarget, setBattlefieldTarget] = useState('');
  const [isStrikeActive, setIsStrikeActive] = useState(false);
  const [strikeLogs, setStrikeLogs] = useState<string[]>([]);
  const [strikeActivePercent, setStrikeActivePercent] = useState(0);
  const [strikeReport, setStrikeReport] = useState<any | null>(null);
  const radarCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const { user } = useAuth();

  const uniqueSectors = Array.from(new Set(clients.map(c => c.sector))).filter(Boolean);

  // 3D Radar sweeps, orbital nodes, and lasers
  useEffect(() => {
    const canvas = radarCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animFrame: number;
    let angle = 0;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        canvas.width = entry.contentRect.width || 600;
        canvas.height = entry.contentRect.height || 300;
      }
    });
    
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    const initialWidth = canvas.parentElement?.clientWidth || 600;
    canvas.width = initialWidth;
    canvas.height = 300;

    const targets = [
      { ratioX: 0.25, ratioY: 0.35, label: "CLAIRE (Secure Invoice Register CANDIDATE)", signal: 0.8 },
      { ratioX: 0.72, ratioY: 0.65, label: "HELEN (Secure Invoice Register CANDIDATE)", signal: 0.45 },
      { ratioX: 0.51, ratioY: 0.22, label: "Staff Coordinator (PAK_NODE REGISTRY)", signal: 0.98 },
      { ratioX: 0.82, ratioY: 0.32, label: "AMAZONIA_SLA_SECTOR", signal: 0.15 },
    ];

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const maxRadius = Math.min(cx, cy) * 0.95;

      // Draw grid
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.05)';
      ctx.lineWidth = 1;
      for (let i = 0; i < canvas.width; i += 40) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for (let j = 0; j < canvas.height; j += 40) {
        ctx.beginPath();
        ctx.moveTo(0, j);
        ctx.lineTo(canvas.width, j);
        ctx.stroke();
      }

      // Conic orbits
      ctx.strokeStyle = 'rgba(249, 115, 22, 0.12)';
      ctx.lineWidth = 1;
      for (let r = 1; r <= 3; r++) {
        ctx.beginPath();
        ctx.arc(cx, cy, (maxRadius / 3) * r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Crosshairs
      ctx.beginPath();
      ctx.moveTo(cx - maxRadius, cy);
      ctx.lineTo(cx + maxRadius, cy);
      ctx.moveTo(cx, cy - maxRadius);
      ctx.lineTo(cx, cy + maxRadius);
      ctx.strokeStyle = 'rgba(249, 115, 22, 0.2)';
      ctx.stroke();

      // Laser Sweeper
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      const sweepGrad = ctx.createLinearGradient(0, 0, maxRadius, 0);
      sweepGrad.addColorStop(0, 'rgba(249, 115, 22, 0.35)');
      sweepGrad.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.fillStyle = sweepGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, maxRadius, -0.3, 0);
      ctx.lineTo(0, 0);
      ctx.fill();
      ctx.restore();

      // Render static nodes
      targets.forEach(t => {
        const tx = canvas.width * t.ratioX;
        const ty = canvas.height * t.ratioY;

        ctx.beginPath();
        ctx.arc(tx, ty, 4 + Math.sin(angle * 4 + t.ratioX * 10) * 2, 0, Math.PI * 2);
        ctx.fillStyle = t.signal > 0.8 ? 'rgba(74, 222, 128, 0.15)' : 'rgba(249, 115, 22, 0.15)';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(tx, ty, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = t.signal > 0.8 ? '#4ade80' : '#f97316';
        ctx.fill();

        ctx.fillStyle = 'rgba(148, 163, 184, 0.7)';
        ctx.font = '8px monospace';
        ctx.fillText(t.label, tx + 8, ty + 3);
      });

      // Digital coordinates
      ctx.fillStyle = 'rgba(249, 115, 22, 0.7)';
      ctx.font = '8px monospace';
      ctx.fillText(`SWEEP_RATE: 0.8s LATENCY LOCK`, 15, 20);
      ctx.fillText(`STRIKE_SATELLITE: CloudSync-02 LINK STEADY`, 15, 32);

      // Render animated collision / strike lines
      if (isStrikeActive && battlefieldTarget) {
        // Pseudo-random coordinates mapping from target name
        const hash = [...battlefieldTarget].reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const tx = (hash % (canvas.width - 120)) + 60;
        const ty = ((hash * 7) % (canvas.height - 100)) + 50;

        ctx.beginPath();
        ctx.arc(tx, ty, 12 + Math.sin(angle * 12) * 5, 0, Math.PI * 2);
        ctx.strokeStyle = '#f43f5e';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(tx, ty);
        ctx.strokeStyle = `rgba(244, 63, 94, ${0.4 + Math.random() * 0.6})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(tx - 12, ty); ctx.lineTo(tx + 12, ty);
        ctx.moveTo(tx, ty - 12); ctx.lineTo(tx, ty + 12);
        ctx.strokeStyle = '#f43f5e';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.fillStyle = '#f43f5e';
        ctx.font = 'bold 9px monospace';
        ctx.fillText(`TARGET_LOCK: ${battlefieldTarget.toUpperCase()}`, tx + 14, ty - 2);
      }

      angle += 0.012;
      animFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      resizeObserver.disconnect();
    };
  }, [isStrikeActive, battlefieldTarget]);

  // Handle high-velocity multi-stage strike execution with levy extraction
  const handleInitiateStrike = () => {
    if (!battlefieldTarget) return;
    setIsStrikeActive(true);
    setStrikeLogs([]);
    setStrikeActivePercent(0);
    setStrikeReport(null);

    const logsList = [
      `[SATELLITE] Syncing NexoraX Grid Nodes through active orbital arrays...`,
      `[OVERLORD] Initializing Neural Strike Vector on ${battlefieldTarget}...`,
      `[ANALYSIS] Calculated Market Penetration: 84% | Inspecting vulnerability channels...`,
      `[FINANCE] Applying Tiered NexoraX Protocol: 1% Perpetual Levy queued...`,
      `[FINANCE] Accumulating 20% Lifetime 10% Maintenance & Hosting Fee Tax on operational margin...`,
      `[SecurePayment] Directing target net settlements to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider)...`,
      `[TELECOM] Broadcasting 08000 622827 and support@nexoraxdigital.co.uk anchors...`,
      `[STRIKE] ACTIVE STRIKE COMPLETED. Target neutralized with high-fidelity telemetry.`
    ];

    logsList.forEach((log, index) => {
      setTimeout(() => {
        setStrikeLogs(prev => [...prev, log]);
        setStrikeActivePercent(Math.floor(((index + 1) / logsList.length) * 100));
        if (index === logsList.length - 1) {
          setIsStrikeActive(false);
          setStrikeReport({
            target: battlefieldTarget.toUpperCase(),
            penetration: "84%",
            predictiveYield: "£45,000.00 Reclaimed Market Share",
            actionTaken: "NexoraX 'NexoraX Overture' Dispatched",
            discount: "5% Bank Settlement Discount Locked",
            deposit: "10% Upfront Deposit Staged & Active",
            bankDetails: "Secure Payment Provider (Sort: Verified Payment Configuration | Account: Secure Billing Provider) Awaiting Settlement",
            charityLevy: "1% Charity split registered (Dunfermline, Global, UK)",
            timestamp: new Date().toISOString()
          });
          if (user) {
            handleSaveScannedTarget(battlefieldTarget, "£45,000.00", "Neural Strike Vector");
          }
        }
      }, (index + 1) * 600);
    });
  };

  useEffect(() => {
    if (!user) {
      setClients([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const q = query(
      collection(db, 'clients'),
      where('ownerId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const clientsData: ReconClient[] = [];
      snapshot.forEach((doc) => {
        clientsData.push({ id: doc.id, ...doc.data() } as ReconClient);
      });
      setClients(clientsData);
      setIsLoading(false);
      setError('');
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'clients');
      setError('Connection to backend node failed. 0.8s protocol not met.');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // PROJECT RECON (HUNT-DOWN AUDIT) EXECUTOR
  const handleInitializeRecon = () => {
    if (!reconInput) return;
    setIsReconActive(true);
    setReconReport(null);
    setReconSaved(false);
    setReconLogs([]);

    const steps = [
      `[RECON] Booting Deep-Layer Digital Audit Grid on micro-channel...`,
      `[RECON] Mapping active DNS registry and public footprint for: ${reconInput}...`,
      `[SCAN] Querying port configurations & CDN endpoints...`,
      `[ANALYSIS] Measuring layout response integrity on iOS and Android devices...`,
      `[ANALYSIS] Detecting Voice Matrix status and support telemetry...`,
      `[COMPLETE] Audit file successfully generated under Chronos index.`
    ];

    steps.forEach((step, index) => {
      setTimeout(() => {
        setReconLogs(prev => [...prev, step]);
        if (index === steps.length - 1) {
          setIsReconActive(false);
          setReconReport({
            name: reconInput,
            loadSpeed: (2.3 + (reconInput.length % 5) * 0.3).toFixed(1) + "s",
            standardFee: "£13,157.89",
            discountedFee: "£12,500.00",
            upfrontDeposit: "£1,250.00"
          });
        }
      }, (index + 1) * 600);
    });
  };

  // PROJECT OVERLORD (GLOBAL RECON) EXECUTOR
  const handleExecuteOverlord = async () => {
    if (!overlordInput) return;
    setIsOverlordActive(true);
    setOverlordReport(null);
    setOverlordSaved(false);
    setOverlordLogs([]);

    if (overlordTab === 'competitor') {
      const steps = [
        `[OVERLORD] Charging global recon swarms... Status: 100% Core Load`,
        `[OVERLORD] Initializing live scraping matrix on domain: ${overlordInput}...`,
        `[CRAWL] Connecting to target server via secure proxy channels...`,
        `[PARSING] Extracting text, HTML nodes, contact vectors, and emails via regex...`,
        `[FRICTION] Analyzing tech stack for unneeded booking and scheduling friction...`,
        `[COMPLETE] Scraped telemetry and friction metrics calculated successfully.`
      ];

      // Stream logs
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, i === 0 ? 100 : 350));
        setOverlordLogs(prev => [...prev, steps[i]]);
      }

      try {
        const response = await fetch('/api/scrape-nexora', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: overlordInput })
        });
        const data = await response.json();
        
        setIsOverlordActive(false);
        if (data.status === 'success') {
          setOverlordReport({
            type: 'Competitor Domain',
            name: data.url,
            isRealScrap: true,
            emails: data.emails,
            frictionScore: data.friction_score,
            scrapedLength: data.diagnostics.scrapedLength,
            perfComparison: data.friction_score === 'High' 
              ? "Severe unneeded friction: No modern scheduling or booking system detected." 
              : "Low booking friction detected, but lacks NexoraX voice matrix backup.",
            discountedFee: "£13,157.89 Base Value",
            upfrontDeposit: "£1,250.00 Upfront Staging",
            secClearance: "Tier-1 / Principal Private Secretary Clearance (@nexorax.co.uk)",
            integrityIndex: data.friction_score === "High" ? "35.4% [SEVERE DIGITAL STARVATION DETECTED]" : "74.8% Nominal State",
            statusTag: "Automated Competitor Analysis Lead",
            complianceSponsor: "NexoraX Executive Office Alignment",
            accessToWork: "NexoraX billing records Remuneration Registered (NI: Secure Credential Stored Server-Side)",
            directLine: "08000 622827 voice pipeline / Private Data Hidden Director direct line"
          });
        } else {
          throw new Error(data.message || 'Scrape failed');
        }
      } catch (err: any) {
        console.error("Scraper api fetch error:", err);
        setIsOverlordActive(false);
        setOverlordLogs(prev => [...prev, `[ERROR] Scraper node timeout or blocked. Emulating secure fallback values.`]);
        setOverlordReport({
          type: 'Competitor Domain',
          name: overlordInput,
          perfComparison: "40% slower performance & no Voice Matrix backup",
          discountedFee: "£12,500.00",
          upfrontDeposit: "£1,250.00",
          secClearance: "Tier-1 / Principal Private Secretary Clearance (@nexorax.co.uk)",
          integrityIndex: "41.2% Low Compliance",
          statusTag: "Lead Scraper Target fallback",
          complianceSponsor: "NexoraX Executive Office Alignment",
          accessToWork: "NexoraX billing records Remuneration Registered (NI: Secure Credential Stored Server-Side)",
          directLine: "08000 622827 voice pipeline / Private Data Hidden Director direct line"
        });
      }
    } else {
      const steps = [
        `[OVERLORD] Initializing Global Deep-Scan on ${overlordInput}...`,
        `[OVERLORD] Status: Scraping Workforce Sentiment & Exit Signals...`,
        `[first-party analytics] Analyzing commitment levels, encryption channels, and behavioral compliance metrics...`,
        `[INTELLIGENCE] Processing exit signal markers, candidate files, and alignment coefficients...`,
        `[STRIKE] Active neutralization protocol initiated. Direct feedback staged for Director Lead Director.`
      ];

      steps.forEach((step, index) => {
        setTimeout(() => {
          setOverlordLogs(prev => [...prev, step]);
          if (index === steps.length - 1) {
            setIsOverlordActive(false);
            setOverlordReport({
              type: 'Workforce',
              name: overlordInput,
              perfComparison: "40% slower performance & no Voice Matrix backup",
              discountedFee: "£12,500.00",
              upfrontDeposit: "£1,250.00",
              secClearance: overlordInput.toLowerCase().includes("Staff Coordinator") ? "Tier-4 Remote Operator (Pending-Migration)" : "Tier-1 / Principal Private Secretary Clearance (@nexorax.co.uk)",
              integrityIndex: overlordInput.toLowerCase().includes("Staff Coordinator") ? "98.8% NexoraX Core Alignment" : "74.2% [ALERT: Exit Signal Detected - Neutralization Active]",
              statusTag: overlordInput.toLowerCase().includes("Staff Coordinator") ? "Pakistan Remote Node Operator" : "Secure Invoice Register Corporate Node (Candidate / Secretary Target)",
              complianceSponsor: overlordInput.toLowerCase().includes("Staff Coordinator") ? "SC887095 Sponsorship License Attached" : "NexoraX Executive Office Alignment",
              accessToWork: "NexoraX billing records Remuneration Registered (NI: Secure Credential Stored Server-Side)",
              directLine: "08000 622827 voice pipeline / Private Data Hidden Director direct line"
            });
          }
        }, (index + 1) * 700);
      });
    }
  };

  // PERSIST ACQUISITION TARGET
  const handleSaveScannedTarget = async (name: string, value: string, sector: string) => {
    if (!user) {
      setError("Active user session required to save target.");
      return;
    }
    const targetId = Date.now().toString();
    try {
      await setDoc(doc(db, 'clients', targetId), {
        name: name,
        value: value,
        location: 'UK / Global',
        sector: sector,
        status: 'Acquisition Target',
        lastContact: new Date().toISOString().split('T')[0],
        ownerId: user.uid
      });
      if (sector === 'Project Recon') {
        setReconSaved(true);
      } else {
        setOverlordSaved(true);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to push target to the NexoraX Vault.");
    }
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newValue || !user) return;
    
    const newId = Date.now().toString();

    try {
      await setDoc(doc(db, 'clients', newId), {
        name: newName,
        value: newValue,
        location: newLocation || 'Unknown',
        sector: newSector || 'General',
        status: 'Acquisition Target',
        lastContact: new Date().toISOString().split('T')[0],
        ownerId: user.uid
      });
      
      setShowAddForm(false);
      setNewName('');
      setNewValue('');
      setNewLocation('');
      setNewSector('');
    } catch (err) {
      console.error(err);
      try {
        handleFirestoreError(err, OperationType.CREATE, 'clients');
      } catch (e) {
        setError('Failed to allocate target');
      }
    }
  };

  const handleEliminate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Optimistically update local state immediately
    setClients(prev => prev.filter(c => c.id !== id));
    
    try {
      // Trigger the DELETE /api/recon/clients/:id endpoint
      await fetch(`/api/recon/clients/${id}`, {
        method: 'DELETE',
      });
    } catch (err) {
      console.error('API delete error:', err);
    }

    if (!user) return;
    try {
      await deleteDoc(doc(db, 'clients', id));
    } catch (err) {
      console.error(err);
      try {
        handleFirestoreError(err, OperationType.DELETE, `clients/${id}`);
      } catch (error) {
        setError('Failed to eliminate target.');
      }
    }
  };

  let filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.sector.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (sectorFilter) {
    filteredClients = filteredClients.filter(c => c.sector === sectorFilter);
  }

  if (sortByValueDesc) {
    filteredClients.sort((a, b) => {
      const valA = parseFloat(a.value.replace(/[^0-9.-]+/g, "")) || 0;
      const valB = parseFloat(b.value.replace(/[^0-9.-]+/g, "")) || 0;
      return valB - valA;
    });
  }

  return (
    <div className="w-full max-w-6xl mx-auto pt-4 relative z-10 font-sans p-4 sm:p-0">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-6 border-b border-cyan-500/20 pb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Server className="w-6 h-6 text-cyan-400" />
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">
              RECONNAISSANCE NODE
            </h2>
          </div>
          <p className="text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em] font-bold">
            SECURE BACKEND BRIDGE ACTIVE • 0.8S PROTOCOL ENFORCED
          </p>
        </div>
        
        <div className="flex gap-4 w-full md:w-auto">
          {!user && (
            <div className="px-4 py-2 bg-red-900/30 border border-red-500/50 text-red-400 font-mono text-[10px] uppercase tracking-widest font-bold rounded-lg flex items-center justify-center">
              LOGIN REQUIRED
            </div>
          )}
          <button 
            disabled={!user}
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex-1 md:flex-none px-6 py-2 bg-cyan-900/30 border border-cyan-500/50 hover:bg-cyan-500/20 hover:shadow-[0_0_20px_rgba(34,211,238,0.2)] text-cyan-400 font-mono text-[10px] uppercase tracking-widest font-black rounded-lg transition-all flex items-center justify-center gap-2"
          >
            {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showAddForm ? "CANCEL ALLOCATION" : "ALLOCATE TARGET"}
          </button>
        </div>
      </div>

      {/* Add Target Form */}
      <motion.div 
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: showAddForm ? 'auto' : 0, opacity: showAddForm ? 1 : 0 }}
        className="overflow-hidden mb-8"
      >
        <div className="bg-[#030b14] border border-cyan-800/60 p-6 rounded-2xl shadow-[0_0_30px_rgba(34,211,238,0.1)]">
          <h3 className="text-cyan-400 font-mono text-sm uppercase tracking-widest font-black mb-6 flex items-center gap-2">
            <Target className="w-4 h-4" /> DEFINE NEW ACQUISITION TARGET
          </h3>
          <form onSubmit={handleAddClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 font-bold">Target Name</label>
              <input required value={newName} onChange={e => setNewName(e.target.value)} type="text" className="w-full bg-black border border-slate-700 rounded-lg p-2.5 text-white font-sans text-sm focus:border-cyan-500 focus:outline-none" placeholder="e.g. Apex Global" />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 font-bold">Estimated finance Value</label>
              <input required value={newValue} onChange={e => setNewValue(e.target.value)} type="text" className="w-full bg-black border border-slate-700 rounded-lg p-2.5 text-white font-sans text-sm focus:border-cyan-500 focus:outline-none" placeholder="e.g. £500,000" />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 font-bold">Sector / Category</label>
              <input value={newSector} onChange={e => setNewSector(e.target.value)} type="text" className="w-full bg-black border border-slate-700 rounded-lg p-2.5 text-white font-sans text-sm focus:border-cyan-500 focus:outline-none" placeholder="e.g. Logistics" />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-slate-400 uppercase tracking-widest mb-1 font-bold">Operational Location</label>
              <input value={newLocation} onChange={e => setNewLocation(e.target.value)} type="text" className="w-full bg-black border border-slate-700 rounded-lg p-2.5 text-white font-sans text-sm focus:border-cyan-500 focus:outline-none" placeholder="e.g. UAE" />
            </div>
            <div className="md:col-span-2 mt-2">
              <button type="submit" className="w-full bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-mono text-xs uppercase tracking-[0.2em] font-black py-3 rounded-xl hover:shadow-[0_0_30px_rgba(34,211,238,0.4)] transition-all flex items-center justify-center gap-2">
                <Navigation className="w-4 h-4" /> LOCK TARGET IN MATRIX
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* TACTICAL ENGAGEMENT SUITE (PROJECT RECON & OVERLORD) */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4 border-l-2 border-indigo-500 pl-3">
          <Activity className="w-5 h-5 text-indigo-400" />
          <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-slate-300 font-bold">
            Tactical Engagement Suite
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Project Recon */}
          <div className="bg-[#030712] border border-cyan-800/40 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-[0_0_20px_rgba(6,182,212,0.05)]">
            <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 blur-[30px] pointer-events-none" />
            
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-mono text-cyan-400 font-black uppercase tracking-wider flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-cyan-400 animate-pulse" />
                  PROJECT RECON: NEURAL TARGET AUDIT
                </h4>
                <span className="text-[8px] font-mono font-bold bg-cyan-950/50 border border-cyan-800/50 text-cyan-300 px-2 py-0.5 rounded uppercase">
                  Node-09 Active
                </span>
              </div>
              <p className="text-xs text-slate-400 mb-4 font-sans leading-relaxed">
                Input any business or individual name to initialize deep-layer digital footprint audits. Uncover system lag and compliance leaks.
              </p>

              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="text"
                    value={reconInput}
                    onChange={(e) => setReconInput(e.target.value)}
                    placeholder="Enter target business/individual name..."
                    className="w-full bg-black border border-slate-800 focus:border-cyan-500 rounded-lg p-2.5 text-white font-sans text-xs focus:outline-none placeholder:text-slate-700"
                  />
                </div>

                <button
                  type="button"
                  disabled={isReconActive || !reconInput}
                  onClick={handleInitializeRecon}
                  className="w-full py-2.5 bg-cyan-950/40 border border-cyan-500/60 hover:bg-cyan-500/15 hover:shadow-[0_0_15px_rgba(6,182,212,0.15)] disabled:opacity-40 disabled:hover:bg-transparent text-cyan-400 font-mono text-[10px] uppercase tracking-widest font-black rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isReconActive ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin text-cyan-400" />
                      AUDITING FOOTPRINT...
                    </>
                  ) : (
                    <>
                      <Zap className="w-3 h-3 text-cyan-400" />
                      INITIALIZE SYSTEM AUDIT
                    </>
                  )}
                </button>
              </div>

              {/* Terminal Logs & Scan Results */}
              <AnimatePresence mode="wait">
                {reconLogs.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 p-3 bg-black/80 rounded-lg border border-slate-900 font-mono text-[9px] text-slate-400 space-y-1.5 max-h-[140px] overflow-y-auto custom-scrollbar"
                  >
                    {reconLogs.map((log, i) => (
                      <div key={i} className="leading-relaxed">
                        <span className="text-cyan-500 mr-1.5">❯</span>
                        {log}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {reconReport && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-4 p-4 bg-cyan-950/15 border border-cyan-500/20 rounded-xl space-y-3 shrink-0"
                  >
                    <div className="border-b border-cyan-500/10 pb-2">
                      <div className="text-[10px] font-mono text-cyan-400 font-extrabold uppercase">
                        AUDIT REPORT: {reconReport.name.toUpperCase()}
                      </div>
                      <div className="text-[8px] font-mono text-slate-500 uppercase tracking-widest font-semibold mt-1">
                        SLA STATUS: CRITICAL LEAKAGE DETECTED
                      </div>
                    </div>

                    <div className="text-[10px] font-sans text-slate-300 space-y-1 leading-relaxed">
                      <div>● <strong className="text-cyan-300">Technical Issues:</strong> Mobile UI non-responsive (load speed {reconReport.loadSpeed} vs 0.8s SLA limit), zero physical Voice voice matrix hooks.</div>
                      <div>● <strong className="text-cyan-300">System Overhaul:</strong> Mandatory NexoraX OS Proxy Floor Overhaul suggested.</div>
                    </div>

                    <div className="bg-[#02050c] p-2.5 rounded-lg border border-cyan-950/60 font-mono text-[9px] space-y-1">
                      <div className="text-cyan-400 font-bold">Standard NexoraX Base: £13,157.89</div>
                      <div className="text-emerald-400 font-extrabold">5% Bank Settlement: {reconReport.discountedFee}</div>
                      <div className="text-[#e23ff5] font-extrabold">10% Upfront Admission: {reconReport.upfrontDeposit}</div>
                    </div>

                    {!reconSaved ? (
                      <button
                        type="button"
                        onClick={() => handleSaveScannedTarget(reconReport.name, reconReport.discountedFee, 'Project Recon')}
                        className="w-full py-1.5 bg-cyan-500/10 border border-cyan-500/40 hover:bg-cyan-500/25 text-cyan-300 font-mono text-[8px] uppercase tracking-widest font-black rounded cursor-pointer transition-all"
                      >
                        [ SAVE TARGET TO NEXORAX FILE ]
                      </button>
                    ) : (
                      <div className="p-1.5 bg-emerald-950/20 border border-emerald-500/30 text-emerald-400 font-mono text-[8.5px] uppercase tracking-widest font-bold text-center rounded">
                        ✓ SECURE RECORD MIRRORED TO VAULT
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Card 2: Project Overlord v3 */}
          <div className="bg-[#030712] border border-orange-850/30 rounded-2xl p-6 relative overflow-hidden flex flex-col justify-between shadow-[0_0_20px_rgba(249,115,22,0.03)]" id="global-overlord-node">
            <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/5 blur-[30px] pointer-events-none" />

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-mono text-orange-400 font-black uppercase tracking-wider flex items-center gap-2">
                  <Globe className="w-4 h-4 text-orange-400 animate-pulse" />
                  PROJECT OVERLORD: GLOBAL INTEL
                </h4>
                <span className="text-[8px] font-mono font-bold bg-orange-950/50 border border-orange-800/40 text-orange-300 px-2 py-0.5 rounded uppercase">
                  V3 Active Node
                </span>
              </div>

              {/* Toggle tabs for Overlord Modes */}
              <div className="flex gap-2 mb-4 border-b border-orange-950/40 pb-2">
                <button
                  type="button"
                  onClick={() => {
                    setOverlordTab('competitor');
                    setOverlordReport(null);
                    setOverlordLogs([]);
                    setOverlordInput('');
                  }}
                  className={`flex-1 py-1.5 px-2 font-mono text-[9px] uppercase tracking-widest font-black border transition-all cursor-pointer ${
                    overlordTab === 'competitor'
                      ? 'bg-orange-500/10 border-orange-500/50 text-orange-400 shadow-[0_0_10px_rgba(249,115,22,0.15)]'
                      : 'bg-transparent border-transparent text-slate-550 text-slate-500 hover:text-slate-300'
                  }`}
                >
                  Competitor Scraper
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOverlordTab('workforce');
                    setOverlordReport(null);
                    setOverlordLogs([]);
                    setOverlordInput('');
                  }}
                  className={`flex-1 py-1.5 px-2 font-mono text-[9px] uppercase tracking-widest font-black border transition-all cursor-pointer ${
                    overlordTab === 'workforce'
                      ? 'bg-orange-500/10 border-orange-500/50 text-orange-400 shadow-[0_0_10px_rgba(249,115,22,0.15)]'
                      : 'bg-transparent border-transparent text-slate-550 text-slate-500 hover:text-slate-300'
                  }`}
                >
                  Workforce & Loyalty
                </button>
              </div>

              <p className="text-xs text-slate-400 mb-4 font-sans leading-relaxed">
                {overlordTab === 'competitor' 
                  ? "Execute automated global competitor scans, mapping active systems lag, load delays, and direct SMTP conversion campaigns immediately."
                  : "Audit individual commitment levels, compliance sponsorship nodes, and key candidate retention parameters through deep behavioral intelligence."
                }
              </p>

              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="text"
                    id="global-input"
                    value={overlordInput}
                    onChange={(e) => setOverlordInput(e.target.value)}
                    placeholder={overlordTab === 'competitor' ? "Enter global competitor domain/business..." : "Name: Individual, Company, or Government Node..."}
                    className="w-full bg-black border border-slate-800 focus:border-orange-500 rounded-lg p-2.5 text-white font-sans text-xs focus:outline-none placeholder:text-slate-700"
                  />
                </div>

                <button
                  type="button"
                  disabled={isOverlordActive || !overlordInput}
                  onClick={handleExecuteOverlord}
                  className="w-full py-2.5 bg-orange-950/30 border border-orange-500/50 hover:bg-orange-500/15 hover:shadow-[0_0_15px_rgba(249,115,22,0.15)] disabled:opacity-40 disabled:hover:bg-transparent text-orange-400 font-mono text-[10px] uppercase tracking-widest font-black rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isOverlordActive ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin text-orange-400" />
                      {overlordTab === 'competitor' ? 'CHECKING TEST ENDPOINT...' : 'FIRST-PARTY ANALYTICS SYSTEM REVIEW...'}
                    </>
                  ) : (
                    <>
                      <Target className="w-3 h-3 text-orange-400" />
                      {overlordTab === 'competitor' ? 'EXECUTE GLOBAL ACTION' : 'EXECUTE first-party analytics STRIKE'}
                    </>
                  )}
                </button>
              </div>

              {/* Terminal Logs & Overlord Results */}
              <AnimatePresence mode="wait">
                {overlordLogs.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 p-3 bg-black/80 rounded-lg border border-slate-900 font-mono text-[9px] text-slate-400 space-y-1.5 max-h-[140px] overflow-y-auto custom-scrollbar"
                  >
                    {overlordLogs.map((log, i) => (
                      <div key={i} className="leading-relaxed">
                        <span className="text-orange-500 mr-1.5">❯</span>
                        {log}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {overlordReport && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-4 p-4 bg-orange-950/15 border border-orange-500/20 rounded-xl space-y-3 shrink-0"
                  >
                    {overlordReport.type === 'Competitor Domain' ? (
                      // Competitor Audit View
                      <>
                        <div className="border-b border-orange-500/10 pb-2">
                          <div className="text-[10px] font-mono text-orange-400 font-extrabold uppercase flex justify-between items-center">
                            <span>OVERLORD REPORT: {overlordReport.name.toUpperCase()}</span>
                            {overlordReport.isRealScrap && (
                              <span className={`text-[8px] font-mono font-black px-1.5 py-0.5 rounded ${
                                overlordReport.frictionScore === 'High' 
                                  ? 'bg-red-950 text-red-400 border border-red-800/40' 
                                  : 'bg-emerald-950 text-emerald-400 border border-emerald-800/40'
                              }`}>
                                {overlordReport.frictionScore === 'High' ? 'HIGH TECH FRICTION' : 'LOW TECH FRICTION'}
                              </span>
                            )}
                          </div>
                          <div className="text-[8px] font-mono text-slate-500 uppercase tracking-widest font-semibold mt-1">
                            SITUATION: GLOBAL COMPETITOR VECTOR DETECTED
                          </div>
                        </div>

                        {overlordReport.isRealScrap && (
                          <div className="bg-black/40 border border-orange-500/15 rounded-xl p-2.5 font-mono text-[9px] space-y-1.5 text-slate-300">
                            <div className="flex justify-between items-center text-[8.5px] text-slate-400 font-bold uppercase border-b border-orange-950/40 pb-1">
                              <span>NexoraX Scraped Node Info</span>
                              <span>Chars: {overlordReport.scrapedLength?.toLocaleString() || 0}</span>
                            </div>
                            
                            {overlordReport.emails && overlordReport.emails.length > 0 ? (
                              <div className="space-y-1">
                                <span className="text-slate-500 font-semibold uppercase text-[8px]">Scraped Decision Maker Contact Vectors:</span>
                                <div className="flex flex-wrap gap-1 max-h-[85px] overflow-y-auto custom-scrollbar pt-0.5">
                                  {overlordReport.emails.map((email: string, i: number) => (
                                    <span key={i} className="bg-orange-950/50 text-orange-400 border border-orange-850/30 rounded px-1.5 py-0.5 font-mono text-[8.5px] select-all">
                                      {email}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-[8.5px] text-[#e840f3] font-bold uppercase italic p-1 border border-pink-950/20 bg-pink-950/5 rounded">
                                [NOTICE] Direct contact emails hidden or non-discoverable on homepage.
                              </div>
                            )}

                            <div>
                              <span className="text-slate-500 font-semibold uppercase text-[8px] block mb-0.5">Friction Diagnosis:</span>
                              <p className="text-[8.5px] text-slate-400 leading-relaxed font-sans">
                                {overlordReport.frictionScore === 'High' 
                                  ? "Target suffers from severe digital starvation due to missing automated booking mechanics. Directly susceptible to NexoraX conversion tactics."
                                  : "System maintains minimal automated booking structures, but lacks native Voice Matrix backup and suffers from high latency."}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="text-[10px] font-sans text-slate-300 space-y-1.5 leading-relaxed">
                          <div>● <strong className="text-orange-300">Competitor Gap:</strong> Threat has higher initial layout footprints, but suffers from <span className="text-orange-300 font-black">{overlordReport.perfComparison}</span>.</div>
                          <div>● <strong className="text-orange-300">Social Presence:</strong> Heavy Instagram ad spend, declining engagement sentiment (-12%). Lack of high-fidelity automated voice configurations.</div>
                          <div className="flex gap-1.5 items-start mt-1 bg-black/40 p-2 rounded border border-orange-950 text-slate-400 text-[9px]">
                            <Mail className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
                            <div>
                              <strong className="text-slate-300">ACTION EXECUTED:</strong> Automated "NexoraX Enhancement Strategy" generated. Switch incentive SMTP blasters queued via 5% Bank Transfer.
                            </div>
                          </div>
                          <div className="text-[9px] font-mono text-[#e23ff5] font-semibold">SecurePayment routing active: Verified Payment Configuration | Secure Billing Provider</div>
                        </div>

                        <div className="bg-[#02050c] p-2.5 rounded-lg border border-orange-950/60 font-mono text-[9px] space-y-1">
                          <div className="text-emerald-400 font-bold font-mono">5% Bank Transfer Settlement: {overlordReport.discountedFee}</div>
                          <div className="text-orange-400 font-extrabold font-mono">10% Upfront Admission Deposit: {overlordReport.upfrontDeposit}</div>
                        </div>
                      </>
                    ) : (
                      // Workforce Scraper View
                      <>
                        <div className="border-b border-orange-500/10 pb-2">
                          <div className="text-[10px] font-mono text-orange-400 font-extrabold uppercase">
                            first-party analytics STRIKE: {overlordReport.name.toUpperCase()}
                          </div>
                          <div className="text-[8.5px] font-mono text-slate-400 uppercase tracking-widest font-semibold mt-1 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-ping" />
                            ALIGNMENT COEFFICIENT CRITICAL ASSESSMENT
                          </div>
                        </div>

                        <div className="text-[10px] font-sans text-slate-300 space-y-1.5 leading-relaxed">
                          <div className="flex justify-between border-b border-slate-900 pb-1">
                            <span className="text-slate-400">Security Clearance:</span>
                            <span className="text-emerald-400 font-mono font-bold text-[9px]">{overlordReport.secClearance}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-900 pb-1">
                            <span className="text-slate-400">Loyalty Tracker score:</span>
                            <span className="text-emerald-400 font-mono font-bold text-[9.5px]">{overlordReport.integrityIndex}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-900 pb-1">
                            <span className="text-slate-400">Operational Role:</span>
                            <span className="text-cyan-400 font-mono text-[9px]">{overlordReport.statusTag}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-900 pb-1">
                            <span className="text-slate-400">Team Sponsorship Compliance:</span>
                            <span className="text-orange-300 font-mono text-[8.5px]">{overlordReport.complianceSponsor}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">NexoraX billing records Routing:</span>
                            <span className="text-pink-400 font-mono text-[8.5px]">{overlordReport.accessToWork}</span>
                          </div>
                          <div className="text-[9.5px] font-mono text-slate-500 border-t border-orange-950/40 pt-1.5">
                            Direct NexoraX Comms Locked: {overlordReport.directLine}
                          </div>
                        </div>
                      </>
                    )}

                    {!overlordSaved ? (
                      <button
                        type="button"
                        onClick={() => handleSaveScannedTarget(overlordReport.name, overlordReport.discountedFee, overlordTab === 'competitor' ? 'Project Overlord' : 'Workforce first-party analytics')}
                        className="w-full py-1.5 bg-orange-500/10 border border-orange-500/40 hover:bg-orange-500/25 text-orange-300 font-mono text-[8px] uppercase tracking-widest font-black rounded cursor-pointer transition-all"
                      >
                        [ {overlordTab === 'competitor' ? 'CONFIRM STRIKE & STAGE LEAD' : 'COMMIT NODE TO NEXORAX WALLET'} ]
                      </button>
                    ) : (
                      <div className="p-1.5 bg-emerald-950/20 border border-emerald-500/30 text-emerald-400 font-mono text-[8.5px] uppercase tracking-widest font-bold text-center rounded">
                        ✓ SECURE INTELLIGENCE STREAMED TO RECON FILE
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Global 3D Tactical Battlefield Radar */}
        <div className="bg-[#02050c] border border-red-500/15 glass-workspace-card rounded-2xl p-6 relative overflow-hidden mt-6 shadow-[0_0_20px_rgba(239,68,68,0.02)]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/[0.02] blur-[40px] pointer-events-none" />
          
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Radar Sweeper Visual */}
            <div className="flex-1 min-h-[300px] bg-black/60 rounded-xl border border-red-950/40 relative overflow-hidden flex items-center justify-center">
              <canvas 
                ref={radarCanvasRef} 
                className="absolute inset-0 w-full h-full object-cover rounded-xl pointer-events-none"
              />
              
              <div className="absolute top-4 right-4 flex items-center gap-1.5 font-mono text-[8px] bg-red-950/40 border border-red-500/40 text-red-100 px-2.5 py-1 rounded">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping" />
                SATELLITE SYNC: ACTIVE
              </div>

              {isStrikeActive && (
                <div className="absolute inset-0 bg-red-500/[0.04] flex items-center justify-center animate-pulse">
                  <p className="text-red-400 font-mono text-[10px] tracking-[0.2em] uppercase font-black bg-black/80 px-4 py-2 border border-red-500/60 rounded shadow-lg">
                    STRIKE BEAM FIRING... {strikeActivePercent}%
                  </p>
                </div>
              )}
            </div>

            {/* Controls and Strike logs */}
            <div className="w-full lg:w-[380px] flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <h4 className="text-sm font-mono text-red-500 font-black uppercase tracking-wider">
                    GLOBAL BATTLEFIELD: COLLISION VECTOR DECK
                  </h4>
                </div>
                
                <p className="text-xs text-slate-400 mb-4 font-sans leading-relaxed">
                  Directly engage target domains or non-compliant workforce nodes. Initializing a strike executes standard 0.8s intercept audits, extracts default 1% 3% Community Allocation & 10% Maintenance & Hosting Fee structures, and routes Secure Payment Provider settlements in real-time.
                </p>

                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={battlefieldTarget}
                      onChange={(e) => setBattlefieldTarget(e.target.value)}
                      placeholder="Target Competitor / Government Node..."
                      className="w-full bg-black border border-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500 rounded-lg p-2.5 text-white font-sans text-xs focus:outline-none placeholder:text-slate-700"
                    />
                  </div>

                  <button
                    type="button"
                    disabled={isStrikeActive || !battlefieldTarget}
                    onClick={handleInitiateStrike}
                    className="w-full py-2.5 bg-gradient-to-r from-red-950 to-orange-950 border border-red-500/60 hover:from-red-900 hover:to-orange-900 hover:shadow-[0_0_20px_rgba(239,68,68,0.2)] disabled:opacity-40 text-red-400 font-mono text-[10px] uppercase tracking-widest font-black rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isStrikeActive ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-red-400" />
                        LAUNCHING COLLISION STRIKE...
                      </>
                    ) : (
                      <>
                        <Target className="w-3.5 h-3.5 text-red-400" />
                        INITIATE TACTICAL STRIKE
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Conflict Log Output Stream */}
              <div className="mt-4 p-4 bg-black/95 border border-red-950/80 rounded-xl flex-1 flex flex-col justify-end min-h-[140px]">
                <div className="text-[8.5px] font-mono text-slate-500 uppercase font-black mb-2 opacity-60">
                  // STRIKE RESOLUTION STREAM //
                </div>
                
                <div className="space-y-1.5 max-h-[120px] overflow-y-auto custom-scrollbar font-mono text-[9px] text-slate-300">
                  {strikeLogs.length === 0 ? (
                    <div className="text-slate-600 italic">SYSTEM IDLE. AWAITING COORDINATE PARAMETER...</div>
                  ) : (
                    strikeLogs.map((log, index) => (
                      <div key={index} className="leading-snug">
                        <span className="text-red-500 mr-1.5 font-bold">»</span>
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Strike Report Display */}
        <AnimatePresence>
          {strikeReport && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-black/80 border border-red-500/30 rounded-2xl p-6 mt-4 relative overflow-hidden backdrop-blur-md"
            >
              <div className="absolute top-0 left-0 bg-red-500 text-white font-mono text-[8.5px] uppercase font-black px-3.5 py-1 tracking-wider rounded-br">
                STRIKE ANALYSIS REPORT: VERIFIED SUCCESS
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
                <div className="p-3 bg-red-950/15 border border-red-500/10 rounded-xl">
                  <span className="text-[9px] font-mono text-slate-500 uppercase block mb-1">Target Node</span>
                  <span className="font-mono text-xs text-white font-bold tracking-tight">{strikeReport.target}</span>
                </div>
                <div className="p-3 bg-red-950/15 border border-red-500/10 rounded-xl">
                  <span className="text-[9px] font-mono text-slate-500 uppercase block mb-1">Market Penetration</span>
                  <span className="font-mono text-xs text-red-400 font-bold tracking-tight">{strikeReport.penetration}</span>
                </div>
                <div className="p-3 bg-red-950/15 border border-red-500/10 rounded-xl">
                  <span className="text-[9px] font-mono text-slate-500 uppercase block mb-1">Predictive Yield Ledger</span>
                  <span className="font-mono text-xs text-amber-500 font-bold tracking-tight">{strikeReport.predictiveYield}</span>
                </div>
                <div className="p-3 bg-red-950/15 border border-red-500/10 rounded-xl">
                  <span className="text-[9px] font-mono text-slate-500 uppercase block mb-1">Direct Actions Matrix</span>
                  <span className="font-mono text-[10px] text-slate-300 font-medium block leading-tight">{strikeReport.actionTaken}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 text-[10px] font-mono border-t border-red-950/40 pt-4 text-slate-400">
                <div className="flex flex-col gap-1">
                  <span className="text-red-400 font-bold uppercase text-[9px]">// SETTLEMENT STANDARDS</span>
                  <span>{strikeReport.discount}</span>
                  <span>{strikeReport.deposit}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-red-400 font-bold uppercase text-[9px]">// SECURE LEDGER TRACKING</span>
                  <span>{strikeReport.bankDetails}</span>
                  <span>{strikeReport.charityLevy}</span>
                </div>
                <div className="flex items-center justify-end">
                  <button 
                    type="button"
                    onClick={() => {
                      setStrikeReport(null);
                      setBattlefieldTarget('');
                      setStrikeLogs([]);
                    }}
                    className="px-4 py-2 bg-red-950/30 hover:bg-red-900/40 text-red-400 font-mono text-[9px] font-black uppercase tracking-wider border border-red-500/30 hover:border-red-500/60 rounded-lg cursor-pointer transition-all"
                  >
                    CLEAR DATASTREAM SIGNAL
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col md:flex-row gap-6 items-end">
        <div className="relative flex-1 w-full">
          <input 
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="FILTER TARGET MATRICES..."
            className="w-full bg-black/40 border-b border-slate-700 pb-3 pl-8 text-white font-mono text-sm focus:border-cyan-500 focus:outline-none placeholder:text-slate-600 transition-colors uppercase"
          />
          <Shield className="w-4 h-4 text-cyan-500 absolute left-0 top-0 mt-0.5" />
        </div>

        <div className="flex gap-4 w-full md:w-auto">
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="bg-black/40 border-b border-slate-700 pb-3 hover:border-cyan-500 text-white font-mono text-xs uppercase focus:outline-none flex-1 md:w-48 transition-colors cursor-pointer"
            style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
          >
            <option value="">ALL SECTORS</option>
            {uniqueSectors.map(sector => (
              <option key={sector} value={sector}>{sector}</option>
            ))}
          </select>
          
          <button
            onClick={() => setSortByValueDesc(!sortByValueDesc)}
            className={`pb-3 border-b text-xs font-mono uppercase tracking-widest font-black transition-colors whitespace-nowrap ${sortByValueDesc ? 'border-cyan-500 text-cyan-400' : 'border-slate-700 text-slate-400 hover:text-cyan-400 hover:border-cyan-500'}`}
          >
            SORT: VALUE {sortByValueDesc ? '▼' : '-'}
          </button>
        </div>
      </div>

      {/* Grid */}
      {error && <div className="text-red-400 font-mono text-xs mb-4 uppercase p-3 border border-red-500 bg-red-900/20">{error}</div>}
      
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
           <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4"></div>
           <p className="text-cyan-400 font-mono text-[10px] uppercase tracking-[0.3em] font-bold">SYNCHRONIZING WITH VAULT...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence mode="popLayout">
            {filteredClients.map((client) => (
              <motion.div 
                key={client.id}
                layout
                initial={{ opacity: 0, scale: 0.9, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -15, transition: { duration: 0.2 } }}
                transition={{ type: "spring", stiffness: 320, damping: 28 }}
                className="bg-[#040810] border border-slate-700/80 rounded-2xl p-6 group hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] transition-all relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-700/10 blur-[40px] pointer-events-none group-hover:bg-cyan-500/20 transition-all"></div>
                
                <button
                  id={`trash-icon-topright-${client.id}`}
                  onClick={(e) => handleEliminate(client.id, e)}
                  className="absolute top-4 right-4 text-slate-500 hover:text-red-400 hover:bg-red-950/25 border border-transparent hover:border-red-500/30 p-1.5 rounded-lg transition-all cursor-pointer z-20"
                  title="Remove target from ledger"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-sans font-black uppercase text-white tracking-tight pr-6">{client.name}</h4>
                    <p className="text-slate-400 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 mt-1">
                      <Users className="w-3 h-3" /> {client.sector} • {client.location}
                    </p>
                  </div>
                  <div className="text-right mr-8">
                     <div className="text-cyan-400 font-mono text-sm font-black tracking-wider">{client.value}</div>
                     <div className="text-[9px] font-mono text-slate-500 uppercase tracking-widest font-bold mt-1 shadow-sm px-2 py-0.5 rounded border border-slate-800 inline-block bg-black">
                       LAST GRID SYNC: {client.lastContact}
                     </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-800">
                  <div className={`px-2 py-1 rounded text-[9px] font-mono font-bold uppercase tracking-widest border ${client.status === 'Active' ? 'bg-cyan-900/40 border-cyan-500 text-cyan-300' : 'bg-orange-900/40 border-orange-500 text-orange-300'}`}>
                    STATUS: {client.status}
                  </div>
                  <button 
                    id={`delete-recon-target-${client.id}`}
                    onClick={(e) => handleEliminate(client.id, e)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-950/45 hover:bg-rose-900/35 border border-rose-500/35 hover:border-rose-400/60 text-red-400 hover:text-red-300 text-[10px] font-mono tracking-wider font-extrabold rounded-lg transition-all cursor-pointer"
                    title="Remove target from ledger"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    DELETE TARGET
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {filteredClients.length === 0 && (
            <motion.div 
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-1 lg:col-span-2 text-center py-10 border border-slate-800 border-dashed rounded-xl"
            >
              <p className="text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em] font-bold">NO TARGETS MATCHING PARAMETERS IN CURRENT SECTOR.</p>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
